import React from 'react'
import { useLocation } from 'react-router-dom'
import useSWR from 'swr'

import UpgradeToPremiumLink from '../../../billing/UpgradeToPremiumLink'
import NewInvoiceLink from '../../../billing/NewInvoiceLink'
import FeedbackNavLink from '../../../feedback/FeedbackNavLink'
import { getPlanLimits } from '../../../../api'
import { useAuth } from '../../../../context/auth'
import useScreenSize from '../../../../context/screenSize'
import { useLayoutPanelsOptional } from '../../../../context/layoutPanels'
import IconButton from '../../button/icon'
import TinyInvoiceLogo from '../../logo'
import SideNavLink from '../../sideNavLink'
import { ReactComponent as MenuBurgerLogo } from '../../../../static/images/menu-burger.svg'
import { ReactComponent as CloseIcon } from '../../../../static/images/close.svg'
import { ReactComponent as MoneyIcon} from '../../../../static/images/money.svg'
import { ReactComponent as LogoutIcon } from '../../../../static/images/logout.svg'
import { ReactComponent as ClientIcon } from '../../../../static/images/client.svg'
import { ReactComponent as SettingsIcon } from '../../../../static/images/settings.svg'
import { ReactComponent as BillingIcon } from '../../../../static/images/wallet.svg'
import { ReactComponent as QuestionIcon } from '../../../../static/images/question.svg'
import './styles.scss'

/**
 * In mobile view, the header content is displayed alongside the menu icon.
 */
type Props = { hideMenus?: boolean, headerContent: React.ReactElement }

const SideBar: React.FC<Props> = ({ hideMenus, headerContent }) => {
  const location = useLocation()
  const layoutPanels = useLayoutPanelsOptional()
  const [localSidebarOpen, setLocalSidebarOpen] = React.useState(false)
  const isOpen = layoutPanels?.sidebarIsOpen ?? localSidebarOpen
  const { user, signout } = useAuth()
  const { isSidebarCollapsed } = useScreenSize()
  const { data: planLimits, isLoading: planLimitsLoading } = useSWR('/api/plan-limits', getPlanLimits)

  React.useEffect(() => {
    if (isOpen && isSidebarCollapsed) {
      document.body.classList.add('mobile-sidebar-open')
    } else {
      document.body.classList.remove('mobile-sidebar-open')
    }
    return () => {
      document.body.classList.remove('mobile-sidebar-open')
    }
  }, [isOpen, isSidebarCollapsed])

  React.useEffect(() => {
    if (!isOpen) return

    const scrollY = window.scrollY
    document.body.classList.add('no-scroll')
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'

    return () => {
      document.body.classList.remove('no-scroll')
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      window.scrollTo(0, scrollY)
    }
  }, [isOpen])

  // Close the sidebar on navigation only. `layoutPanels` is a memoized context
  // value whose reference changes whenever `sidebarIsOpen` flips, so depending on
  // it here would immediately re-close the sidebar the instant it opens.
  const layoutPanelsRef = React.useRef(layoutPanels)
  layoutPanelsRef.current = layoutPanels

  React.useEffect(() => {
    layoutPanelsRef.current?.closeSidebar()
    setLocalSidebarOpen(false)
  }, [location])

  const showUpgradeToPremium =
    !!user &&
    !user.hasActiveSubscription &&
    !planLimits?.freeAccess &&
    !hideMenus &&
    !planLimitsLoading

  const toggleSideBar = () => {
    if (layoutPanels) {
      layoutPanels.toggleSidebar()
      return
    }
    setLocalSidebarOpen((v) => !v)
  }

  const showMobileBar = !isOpen && isSidebarCollapsed

  if (showMobileBar) {
    return (
      <div className='row align-items-center fixed-top closed-sidebar-container py-3 px-3 px-md-4'>
        <div className='col-1'><IconButton onClick={toggleSideBar} icon={<MenuBurgerLogo width={23} height={23} />} /></div>
        <div className='col-11 d-flex align-items-center px-md-4 min-w-0'>{headerContent}</div>
      </div>
    )
  }

  return (
    <div className='sidebar-container'>
      <div className='d-flex align-items-start justify-content-between flex-shrink-0'>
        <TinyInvoiceLogo />
        { isSidebarCollapsed && <IconButton icon={<CloseIcon width={30} height={30} />} onClick={toggleSideBar} />}
      </div>
      <div className='mt-4 sidebar-main gap-5'>
        {!hideMenus && (
          <div className='sidebar-nav-top'>
            <div className='mt-2'><SideNavLink icon={<MoneyIcon />} to='/dashboard'>Dashboard</SideNavLink></div>
            <div className='mt-2'><SideNavLink icon={<SettingsIcon />} to='/profile'>Profile</SideNavLink></div>
            <div className='mt-2'><SideNavLink icon={<ClientIcon />} to='/clients'>Clients</SideNavLink></div>
            {user?.stripeCustomerId && (
              <div className='mt-2'><SideNavLink icon={<BillingIcon />} to='/billing'>Billing</SideNavLink></div>
            )}
            <div className='mt-2'><SideNavLink icon={<QuestionIcon />} to='/faq'>FAQs</SideNavLink></div>
            <div className='mt-2'><FeedbackNavLink /></div>
            <div className='mt-5 sidebar-new-invoice'>
              <NewInvoiceLink />
            </div>
          </div>
        )}
        <div className='sidebar-nav-spacer' aria-hidden />
        <div className='sidebar-nav-footer d-flex flex-column gap-3 flex-shrink-0'>
          {showUpgradeToPremium && (
            <UpgradeToPremiumLink />
          )}
          <SideNavLink onClick={signout} icon={<LogoutIcon />}>Logout</SideNavLink>
        </div>
      </div>
    </div>
  )
}

export default SideBar
